import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Minus, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Preorder() {
  const [searchParams] = useSearchParams();
  const item = searchParams.get("item") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/preorders`, {
        name: name.trim(),
        email: email.trim(),
        country: country.trim(),
        quantity,
        note: note.trim(),
        item,
      });
      setConfirmation(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none transition-colors duration-300 focus:border-neutral-900";
  const labelCls = "text-[10px] uppercase tracking-[0.25em] text-neutral-500";

  return (
    <motion.main
      data-testid="preorder-page"
      className="relative flex min-h-screen w-full flex-col bg-white text-neutral-900"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <header className="relative z-20 px-5 pt-20 sm:px-10">
        <Link
          to="/home"
          data-testid="back-to-home-link"
          className="group inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-neutral-500 transition-colors duration-300 hover:text-neutral-900 sm:text-xs"
        >
          <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
          Back to studio
        </Link>
      </header>

      <div className="relative z-20 flex flex-1 items-center justify-center px-5 py-10">
        <AnimatePresence mode="wait">
          {confirmation ? (
            <motion.div
              key="success"
              data-testid="preorder-confirmation"
              className="flex flex-col items-center gap-6 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">You&apos;re in.</h1>
              <div
                data-testid="order-id"
                className="rounded-full border border-neutral-900 bg-neutral-900 px-6 py-2.5 text-xs uppercase tracking-[0.35em] text-white sm:text-sm"
              >
                {confirmation.order_id}
              </div>
              {confirmation.email_sent && (
                <p data-testid="email-sent-note" className="max-w-xs text-xs leading-relaxed text-neutral-500">
                  Your order code is on its way to <span className="font-semibold text-neutral-900">{email}</span>
                </p>
              )}
              <Link
                to="/home"
                data-testid="confirmation-back-link"
                className="rounded-full border border-neutral-300 px-7 py-3 text-xs uppercase tracking-[0.25em] text-neutral-600 transition-colors duration-300 hover:border-neutral-900 hover:text-neutral-900"
              >
                Back to studio
              </Link>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              onSubmit={handleSubmit}
              data-testid="preorder-form"
              className="flex w-full max-w-md flex-col gap-5 rounded-2xl border border-neutral-200 bg-white p-6 sm:p-9"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 className="text-lg font-bold tracking-tight">Pre-order</h1>
              {item && (
                <p
                  data-testid="preorder-item"
                  className="-mt-3 text-[10px] uppercase tracking-[0.3em] text-neutral-500"
                >
                  Reserving: <span className="font-semibold text-neutral-900">{item}</span>
                </p>
              )}

              <label className="flex flex-col gap-2">
                <span className={labelCls}>Full name</span>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="preorder-name-input"
                  placeholder="Daisy Doe"
                  className={inputCls}
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className={labelCls}>Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="preorder-email-input"
                  placeholder="you@bloom.com"
                  className={inputCls}
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className={labelCls}>Shipping country</span>
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="preorder-country-input"
                  placeholder="Where should it ship?"
                  className={inputCls}
                />
              </label>

              <div className="flex flex-col gap-2">
                <span className={labelCls}>Quantity</span>
                <div className="flex items-center gap-4" data-testid="preorder-quantity-select">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    data-testid="quantity-minus-button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-colors duration-300 hover:border-neutral-900 hover:text-neutral-900"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <motion.span
                    key={quantity}
                    data-testid="quantity-value"
                    className="w-8 text-center text-2xl font-bold text-neutral-900"
                    initial={{ scale: 1.25, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 18 }}
                  >
                    {quantity}
                  </motion.span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    data-testid="quantity-plus-button"
                    onClick={() => setQuantity((q) => Math.min(5, q + 1))}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-colors duration-300 hover:border-neutral-900 hover:text-neutral-900"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">max 5</span>
                </div>
              </div>

              <label className="flex flex-col gap-2">
                <span className={labelCls}>
                  Note to studio <span className="text-neutral-400">(optional)</span>
                </span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="preorder-note-input"
                  rows={3}
                  placeholder="Anything we should know?"
                  className={`${inputCls} resize-none`}
                />
              </label>

              {error && (
                <motion.p
                  data-testid="preorder-error"
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {error}
                </motion.p>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="preorder-submit-button"
                className="mt-2 rounded-full bg-neutral-900 px-8 py-4 text-xs font-bold uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-neutral-700 disabled:opacity-60"
              >
                {submitting ? "Reserving…" : "Reserve my piece"}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </motion.main>
  );
}
